/**
 * MSAL.js setup for Azure AD / Office 365 sign-in.
 *
 * The frontend drives the auth dance: it redirects to Microsoft, Microsoft
 * sends the user back with an authorization code, the callback page
 * extracts the code + PKCE verifier and POSTs them to our backend, which
 * completes the exchange and sets the httpOnly session cookie.
 *
 * We do NOT store the Microsoft access token or id token client-side. The
 * only auth state the browser keeps is the httpOnly cookie from our
 * backend. This minimizes the client-side attack surface.
 */

import {
  PublicClientApplication,
  type Configuration,
  type AuthenticationResult,
  BrowserAuthError,
} from '@azure/msal-browser';

const tenantId = import.meta.env.VITE_AZURE_AD_TENANT_ID;
const clientId = import.meta.env.VITE_AZURE_AD_CLIENT_ID;
const redirectUri = import.meta.env.VITE_AZURE_AD_REDIRECT_URI;

const msalConfig: Configuration = {
  auth: {
    clientId,
    authority: `https://login.microsoftonline.com/${tenantId}`,
    redirectUri,
    postLogoutRedirectUri: '/login',
    // We do NOT navigate to the requested app URL after MSAL returns —
    // our callback page handles the code exchange and then navigates.
    navigateToLoginRequestUrl: false,
  },
  cache: {
    // Session storage is a deliberate choice — nothing long-lived stored
    // in the browser. The auth state of record is the backend JWT cookie.
    cacheLocation: 'sessionStorage',
    storeAuthStateInCookie: false,
  },
  system: {
    loggerOptions: {
      loggerCallback: () => void 0,
      piiLoggingEnabled: false,
    },
  },
};

export const msalInstance = new PublicClientApplication(msalConfig);

let initialised = false;
export async function initMsal(): Promise<void> {
  if (initialised) return;
  await msalInstance.initialize();
  // Drain any pending redirect-flow state from a previous attempt.
  // Without this, a user who closed the browser mid-login (or whose
  // MSAL-side flow errored after the redirect) gets stuck with
  // "interaction_in_progress" on the next loginRedirect call. Calling
  // handleRedirectPromise() unconditionally clears that state — if
  // there's nothing pending, it resolves to null and is harmless.
  try {
    await msalInstance.handleRedirectPromise();
  } catch {
    // Swallow — we don't surface stale-state errors to the user. The
    // actual login flow will surface a clean error if anything is
    // really wrong.
  }
  initialised = true;
}

/**
 * Trigger the Microsoft login redirect. Returns a promise that never
 * resolves in the happy path because the browser navigates away; throws
 * on local failures (popup blocked, invalid config). If MSAL's internal
 * state somehow still claims an interaction is in progress, force-clear
 * and retry once — this handles the rare case where sessionStorage was
 * left dirty.
 */
export async function beginLoginRedirect(): Promise<void> {
  await initMsal();
  try {
    await msalInstance.loginRedirect({
      scopes: ['User.Read', 'profile', 'openid', 'email'],
      prompt: 'select_account',
    });
  } catch (err) {
    const errorCode = (err as { errorCode?: string })?.errorCode ?? '';
    if (errorCode === 'interaction_in_progress') {
      // Force-clear MSAL's interaction status and retry. Documented at
      // https://learn.microsoft.com/azure/active-directory/develop/msal-error-handling-js
      // The deprecated key 'msal.interaction.status' is what MSAL uses
      // internally for this flag.
      try {
        sessionStorage.removeItem('msal.interaction.status');
        for (const key of Object.keys(sessionStorage)) {
          if (key.startsWith('msal.')) sessionStorage.removeItem(key);
        }
      } catch {
        // sessionStorage may be unavailable in some private-browsing modes
      }
      await msalInstance.loginRedirect({
        scopes: ['User.Read', 'profile', 'openid', 'email'],
        prompt: 'select_account',
      });
      return;
    }
    throw err;
  }
}

/**
 * Process the Microsoft redirect callback. Returns the auth result if
 * this was a redirect response, or null if there was nothing to handle.
 * Errors from Microsoft (user cancel, etc.) surface as thrown errors.
 */
export async function handleRedirectResponse(): Promise<AuthenticationResult | null> {
  await initMsal();
  try {
    const result = await msalInstance.handleRedirectPromise();
    return result;
  } catch (err) {
    if (err instanceof BrowserAuthError) throw err;
    throw err;
  }
}
